import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateImages, updateUser, getUser } from '../../../store/user.js';
import SublyApi from '../../../helpers/Api.js';

export const useOnboardingImages = (client) => {
	const dispatch = useDispatch();
	const token = useSelector((st) => st.user.token); // User token for onboarding
	const [uploading, setUploading] = useState({});
	const [error, setError] = useState(null);
	const [images, setImages] = useState({});
	const [loading, setLoading] = useState(false);

	// Load images from API when client changes
	useEffect(() => {
		if (client?.username) {
			loadImages();
		}
	}, [client?.username]);


	// Load all images for the client
	const loadImages = useCallback(async () => {
		if (!client?.username || !token) return;

		setLoading(true);
		try {
			const response = await SublyApi.getUserImages(
				client.username,
				{ organized: true },
				token
			);

			const imageData = response.images || {};
			setImages({ ...imageData });
		} catch (err) {
			console.error('❌ LOADIMAGES FAILED:', err);
			console.error('❌ API ENDPOINT ERROR - Cannot fetch user images');
			setError(err?.message || 'Failed to load images');
			setImages({}); // Initialize empty state on error
		} finally {
			setLoading(false);
		}
	}, [client?.username, token]);

	// Get images by type (with multi-slot support)
	const getImagesByType = useCallback(
		(imageType) => {
			// Map frontend image type to backend key
			const backendKey = imageType === 'broker_logo' ? 'brokerLogo' : imageType;

			// Ensure we always return an array
			const imageData = images[backendKey];

			// If imageData exists and is an array, return it
			if (Array.isArray(imageData)) {
				return imageData;
			}

			// If imageData exists but is not an array, wrap it in an array
			if (imageData) {
				return [imageData];
			}

			// ⚠️ LEGACY FIELDS REMOVED - DO NOT USE client.headshot, client.logo, client.broker_logo
			// These fields are deprecated and should not be used for image data
			// All images must come from the new multi-slot API system only
			// If you need to access legacy data, use a different approach
			
			// Return empty array as fallback
			return [];
		},
		[images] // Removed client dependency since we no longer use legacy fields
	);

	// Get image by slot
	const getImageBySlot = useCallback(
		(imageType, slotNumber) => {
			const imagesByType = getImagesByType(imageType);
			return imagesByType.find((img) => img.imageSlot === slotNumber) || null;
		},
		[getImagesByType]
	);

	// Upload image with multi-slot support
	const uploadImage = useCallback(
		async (imageType, slotNumber, file, options = {}) => {
			if (!client || !file) return false;


			const slotKey = `${imageType}-${slotNumber}`;
			setUploading((prev) => ({ ...prev, [slotKey]: true }));
			setError(null);

			try {
				// Check if slot is occupied
				const existingImage = getImageBySlot(imageType, slotNumber);

				if (existingImage && !existingImage.isLegacy) {
					// Replace existing image
					await SublyApi.replaceUserImage(existingImage.id, file, token);
				} else {
					// Upload new image
					await SublyApi.uploadUserImage(
						client.username,
						{
							file,
							imageType,
							imageSlot: slotNumber,
							caption: options.caption,
							isPrimary: options.isPrimary || slotNumber === 1,
						},
						token
					);

					// If replacing legacy image, clear it from user profile
					if (existingImage?.isLegacy) {
						await dispatch(
							updateUser({
								username: client.username,
								data: { [imageType]: '' },
								token,
							})
						).unwrap();
					}
				}

				// Reload images to get updated data
				await loadImages();

				return true;
			} catch (err) {
				console.error(`Error uploading ${imageType}:`, err);
				setError(err?.message || 'Failed to upload image');
				return false;
			} finally {
				setUploading((prev) => ({ ...prev, [slotKey]: false }));
			}
		},
		[client, token, getImageBySlot, loadImages, dispatch]
	);

	// Remove image (backward compatible)
	const removeImage = useCallback(
		async (imageType, slotNumber = 1) => {
			if (!client) return false;

			const slotKey = `${imageType}-${slotNumber}`;
			setUploading((prev) => ({ ...prev, [slotKey]: true }));
			setError(null);

			try {
				const existingImage = getImageBySlot(imageType, slotNumber);

				if (existingImage && !existingImage.isLegacy) {
					// Delete from new API
					await SublyApi.deleteUserImage(existingImage.id, token);
				}

				// Also clear from legacy user profile for backward compatibility
				await dispatch(
					updateUser({
						username: client.username,
						data: { [imageType]: '' },
						token,
					})
				).unwrap();

				// Reload images
				await loadImages();

				return true;
			} catch (err) {
				console.error(`Error removing ${imageType}:`, err);
				setError(err?.message || 'Failed to remove image');
				return false;
			} finally {
				setUploading((prev) => ({ ...prev, [slotKey]: false }));
			}
		},
		[client, token, getImageBySlot, loadImages, dispatch]
	);

	// Delete image by ID
	const deleteImage = useCallback(
		async (imageId) => {
			console.log('🔥 HOOK deleteImage called with ID:', imageId);
			
			if (!imageId) {
				console.warn('🔥 HOOK deleteImage - No imageId provided');
				return false;
			}

			// Ensure imageId is a string
			const idString = String(imageId);
			console.log('🔥 HOOK deleteImage - converted to string:', idString);

			// Handle legacy ID format: "legacy-username-imageType"
			if (idString.startsWith('legacy-')) {
				console.log(
					'🔥 HOOK deleteImage - Legacy image detected, calling removeImage'
				);
				const parts = idString.split('-');
				const imageType = parts[parts.length - 1];
				return await removeImage(imageType, 1);
			}

			setUploading((prev) => ({ ...prev, [imageId]: true }));
			setError(null);

			try {
				await SublyApi.deleteUserImage(idString, token);
				await loadImages();
				return true;
			} catch (err) {
				console.error('Delete failed:', err);
				setError(err?.message || 'Failed to delete image');
				return false;
			} finally {
				setUploading((prev) => ({ ...prev, [imageId]: false }));
			}
		},
		[token, loadImages, removeImage, client, dispatch]
	);

	// Update caption
	const updateCaption = useCallback(
		async (imageId, caption) => {
			if (!imageId) return false;

			// Skip legacy images
			if (imageId.startsWith('legacy-')) {
				console.warn('Caption updates not supported for legacy images');
				return true;
			}

			try {
				await SublyApi.updateUserImage(imageId, { caption }, token);
				await loadImages();
				return true;
			} catch (err) {
				console.error('Error updating caption:', err);
				setError(err?.message || 'Failed to update caption');
				return false;
			}
		},
		[token, loadImages]
	);

	// Set as primary image
	const setPrimary = useCallback(
		async (imageId) => {
			if (!imageId || String(imageId).startsWith('legacy-')) {
				return false;
			}

			try {
				await SublyApi.updateUserImage(imageId, { isPrimary: true }, token);
				await loadImages();
				return true;
			} catch (err) {
				console.error('Error setting primary image:', err);
				setError(err?.message || 'Failed to set primary image');
				return false;
			}
		},
		[token, loadImages]
	);

	// Get image URL (backward compatible)
	const getImageUrl = useCallback(
		(imageType, slotNumber = 1) => {
			const image = getImageBySlot(imageType, slotNumber);
			return image?.imageUrl || client?.[imageType] || null;
		},
		[getImageBySlot, client]
	);

	// Check if image exists (backward compatible)
	const hasImage = useCallback(
		(imageType, slotNumber = 1) => {
			return Boolean(getImageUrl(imageType, slotNumber));
		},
		[getImageUrl]
	);

	// Get primary image for a type
	const getPrimaryImage = useCallback(
		(imageType) => {
			const imagesByType = getImagesByType(imageType);
			return (
				imagesByType.find((img) => img.isPrimary) || imagesByType[0] || null
			);
		},
		[getImagesByType]
	);

	// Get next available slot
	const getNextAvailableSlot = useCallback(
		async (imageType) => {
			try {
				const response = await SublyApi.getNextAvailableSlot(
					client?.username,
					imageType,
					token
				);
				return response.nextSlot;
			} catch (err) {
				console.error('Error getting next slot:', err);
				return 1; // Default to slot 1
			}
		},
		[client?.username, token]
	);

	// Get reactive image counts for dependency tracking
	const imagesCounts = useMemo(() => {
		// Calculate counts directly from API images state only (NO legacy fallback)
		const headshots = images.headshot || [];
		const personalLogos = images.logo || [];
		const brokerLogos = images.brokerLogo || []; // Note: backend uses brokerLogo key
		const lifestyle = images.lifestyle || [];
		
		const counts = {
			headshots: headshots.length,
			logos: personalLogos.length,
			brokerLogos: brokerLogos.length,
			lifestyle: lifestyle.length,
			total: headshots.length + personalLogos.length + brokerLogos.length + lifestyle.length
		};
		
		
		return counts;
	}, [images]); // Only depend on images state from API

	// Check if minimum required images are uploaded
	const hasMinimumRequiredImages = useCallback(() => {
		// Require at least one of each type: headshot AND personal logo AND broker logo
		return imagesCounts.headshots > 0 && imagesCounts.logos > 0 && imagesCounts.brokerLogos > 0;
	}, [imagesCounts]);

	return {
		// Multi-slot support
		images,
		loading,
		error,
		loadImages,
		uploadImage,
		removeImage,
		deleteImage,
		updateCaption,
		setPrimary,
		getImagesByType,
		getImageBySlot,
		getPrimaryImage,
		getNextAvailableSlot,
		hasMinimumRequiredImages,
		imagesCounts, // Reactive image counts for dependency tracking

		// Backward compatibility
		getImageUrl,
		hasImage,
		uploading,
	};
};